import {
  AdditionalProperties,
  EmptyAdditionalProperties,
  ValidAdditionalProperties,
} from 'ApplicationEntry/models/AdditionalProperties';
import {AgentInformation, EmptyAgent, ValidAgent} from 'ApplicationEntry/models/AgentInformation';
import {AgentList, EmptyAgentList, ValidAgentList} from 'ApplicationEntry/models/AgentList';
import {
  EmptyNewYorkAWDStatus,
  NewYorkAWDStatus,
  ValidNewYorkAWDStatus,
} from 'ApplicationEntry/models/AWDStatus/NewYorkAWDStatus';
import {BillingModes, EmptyBillingModes, ValidBillingModes} from 'ApplicationEntry/models/BillingModes';
import {
  ContingentBeneficiary,
  EmptyContingentBeneficiary,
  ValidContingentBeneficiary,
} from 'ApplicationEntry/models/ContingentBeneficiary';
import {
  ContingentBeneficiaryList,
  EmptyContingentBeneficiaryList,
  ValidContingentBeneficiaryList,
} from 'ApplicationEntry/models/ContingentBeneficiaryList';
import {DependentLists, EmptyDependentList, ValidDependentList} from 'ApplicationEntry/models/DependentLists';
import {Dependents, EmptyDependents, ValidDependents} from 'ApplicationEntry/models/Dependents';
import {EmptyHippaTrustees, HippaTrustees, ValidHippaTrustees} from 'ApplicationEntry/models/HippaTrustees';
import {EmptyMessage, Messages, ValidMessage} from 'ApplicationEntry/models/Messages';
import {
  EmptyNewYorkApplicantDemographics,
  NewYorkApplicantDemographics,
  ValidNewYorkApplicantDemographics,
} from 'ApplicationEntry/models/NewYorkApplicantDemographics';
import {
  EmptyNewYorkBillingAgent,
  NewYorkBillingAgent,
  ValidNewYorkBillingAgent,
} from 'ApplicationEntry/models/NewYorkBillingAgent';
import {
  EmptyNewYorkCaseDetails,
  NewYorkCaseDetails,
  ValidNewYorkCaseDetails,
} from 'ApplicationEntry/models/NewYorkCaseDetails';
import {
  EmptyNewYorkVerifyCriticalInformation,
  NewYorkVerifyCriticalInformation,
  ValidNewYorkVerifyCriticalInformation,
} from 'ApplicationEntry/models/NewYorkVerifyCriticalInformation';
import {EmptyOwnerInfo, OwnerInfo, ValidOwnerInfo} from 'ApplicationEntry/models/OwnerInfo';
import {EmptyPlanCode, PlanCode, ValidPlanCode} from 'ApplicationEntry/models/PlanCode';
import {EmptyPlanCodes, PlanCodes, ValidPlanCodes} from 'ApplicationEntry/models/PlanCodes';
import {EmptyPolicyRecord, PolicyRecord, ValidPolicyRecord} from 'ApplicationEntry/models/PolicyRecord';
import {EmptyPremium, Premium, ValidPremium} from 'ApplicationEntry/models/Premium';
import {
  EmptyPrimaryBeneficiary,
  PrimaryBeneficiary,
  ValidPrimaryBeneficiary,
} from 'ApplicationEntry/models/PrimaryBeneficiary';
import {
  EmptyPrimaryBeneficiaryList,
  PrimaryBeneficiaryList,
  ValidPrimaryBeneficiaryList,
} from 'ApplicationEntry/models/PrimaryBeneficiaryList';
import {
  EmptyNewYorkAccident,
  NewYorkAccident,
  ValidNewYorkAccident,
} from 'ApplicationEntry/models/ProductsRiders/Accident/NewYorkAccident';
import {
  EmptyNewYorkAccidentNewYork,
  NewYorkAccidentNewYork,
  ValidNewYorkAccidentNewYork,
} from 'ApplicationEntry/models/ProductsRiders/AccidentNewYork/NewYorkAccidentNewYork';
import {EmptyNewYorkAIP, NewYorkAIP, ValidNewYorkAIP} from 'ApplicationEntry/models/ProductsRiders/AIP/NewYorkAIP';
import {
  EmptyNewYorkCancer,
  NewYorkCancer,
  ValidNewYorkCancer,
} from 'ApplicationEntry/models/ProductsRiders/Cancer/NewYorkCancer';
import {
  EmptyNewYorkCriticalIllness,
  NewYorkCriticalIllness,
  ValidNewYorkCriticalIllness,
} from 'ApplicationEntry/models/ProductsRiders/CriticalIllness/NewYorkCriticalIllness';
import {
  EmptyNewYorkCriticalIllnessNY,
  NewYorkCriticalIllnessNY,
  ValidNewYorkCriticalIllnessNY,
} from 'ApplicationEntry/models/ProductsRiders/CriticalIllnessNY/NewYorkCriticalIllnessNY';
import {
  EmptyNewYorkDisability,
  NewYorkDisability,
  ValidNewYorkDisability,
} from 'ApplicationEntry/models/ProductsRiders/Disability/NewYorkDisability';
import {EmptyNewYorkGLTERM, NewYorkGLTERM} from 'ApplicationEntry/models/ProductsRiders/GLTERM/NewYorkGLTERM';
import {
  EmptyNewYorkHeartStroke,
  NewYorkHeartStroke,
  ValidNewYorkHeartStroke,
} from 'ApplicationEntry/models/ProductsRiders/HeartStroke/NewYorkHeartStroke';
import {
  EmptyNewYorkICRTN,
  NewYorkICRTN,
  ValidNewYorkICRTN,
} from 'ApplicationEntry/models/ProductsRiders/ICRTN/NewYorkICRTN';
import {EmptyNewYorkIWL, NewYorkIWL, ValidNewYorkIWL} from 'ApplicationEntry/models/ProductsRiders/IWL/NewYorkIWL';
import {EmptyNewYorkShop, NewYorkShop, ValidNewYorkShop} from 'ApplicationEntry/models/ProductsRiders/Shop/NewYorkShop';
import {EmptyNewYorkUL, NewYorkUL} from 'ApplicationEntry/models/ProductsRiders/UniversalLife/NewYorkUL';
import {
  EmptySecondaryAddressee,
  SecondaryAddressee,
  ValidSecondaryAddressee,
} from 'ApplicationEntry/models/SecondaryAddressee';
import {EmptyUWQuestions, UWQuestions, ValidUWQuestions} from 'ApplicationEntry/models/UnderwritingQuestions';
import {ToMatchOptions} from 'Shared/utilities/MapDiscriminatedUnion';
import {ValidGroupGLTERM} from './ProductsRiders/GLTERM/GroupGLTERM';
import {EmptyNewYorkPinCare, NewYorkPinCare, ValidNewYorkPinCare} from './ProductsRiders/PinCare/NewYorkPinCare';
import {ValidGroupUL} from './ProductsRiders/UniversalLife/GroupUL';

type NewYorkProps = {
  caseDetails: NewYorkCaseDetails;
  applicantDemographics: NewYorkApplicantDemographics;
  ownerInfo: OwnerInfo;
  secondaryAddressee: SecondaryAddressee;
  primaryBeneficiaryList: PrimaryBeneficiaryList;
  primaryBeneficiary: PrimaryBeneficiary;
  contingentBeneficiary: ContingentBeneficiary;
  contingentBeneficiaryList: ContingentBeneficiaryList;
  criticalIllness: NewYorkCriticalIllness;
  icrtn: NewYorkICRTN;
  cancer: NewYorkCancer;
  dependents: Dependents;
  dependentLists: DependentLists;
  policyRecord: PolicyRecord;
  billingAgent: NewYorkBillingAgent;
  agentInformation: AgentInformation;
  agentList: AgentList;
  messages: Messages;
  billingModes: BillingModes;
  awdStatus: NewYorkAWDStatus;
  verifyCriticalInformation: NewYorkVerifyCriticalInformation;
  disability: NewYorkDisability;
  shop: NewYorkShop;
  iwl: NewYorkIWL;
  heartStroke: NewYorkHeartStroke;
  accident: NewYorkAccident;
  uwQuestions: UWQuestions;
  universalLife: NewYorkUL;
  glterm: NewYorkGLTERM;
  pinCare: NewYorkPinCare;
  accidentNewYork: NewYorkAccidentNewYork;
  criticalIllnessNY: NewYorkCriticalIllnessNY;
  hippaTrustees: HippaTrustees;
  premium: Premium;
  aip: NewYorkAIP;
  planCode: PlanCode;
  planCodes: PlanCodes;
  additionalProperties: AdditionalProperties;
};

export class InvalidNewYorkApplication {
  public readonly key = 'InvalidNewYorkApplication';

  constructor(
    public readonly caseDetails: NewYorkCaseDetails,
    public readonly applicantDemographics: NewYorkApplicantDemographics,
    public readonly ownerInfo: OwnerInfo,
    public readonly secondaryAddressee: SecondaryAddressee,
    public readonly primaryBeneficiaryList: PrimaryBeneficiaryList,
    public readonly primaryBeneficiary: PrimaryBeneficiary,
    public readonly contingentBeneficiary: ContingentBeneficiary,
    public readonly contingentBeneficiaryList: ContingentBeneficiaryList,
    public readonly criticalIllness: NewYorkCriticalIllness,
    public readonly icrtn: NewYorkICRTN,
    public readonly cancer: NewYorkCancer,
    public readonly dependents: Dependents,
    public readonly dependentLists: DependentLists,
    public readonly policyRecord: PolicyRecord,
    public readonly billingAgent: NewYorkBillingAgent,
    public readonly agentInformation: AgentInformation,
    public readonly agentList: AgentList,
    public readonly messages: Messages,
    public readonly billingModes: BillingModes,
    public readonly awdStatus: NewYorkAWDStatus,
    public readonly verifyCriticalInformation: NewYorkVerifyCriticalInformation,
    public readonly disability: NewYorkDisability,
    public readonly shop: NewYorkShop,
    public readonly iwl: NewYorkIWL,
    public readonly heartStroke: NewYorkHeartStroke,
    public readonly accident: NewYorkAccident,
    public readonly uwQuestions: UWQuestions,
    public readonly universalLife: NewYorkUL,
    public readonly glterm: NewYorkGLTERM,
    public readonly pinCare: NewYorkPinCare,
    public readonly accidentNewYork: NewYorkAccidentNewYork,
    public readonly criticalIllnessNY: NewYorkCriticalIllnessNY,
    public readonly hippaTrustees: HippaTrustees,
    public readonly premium: Premium,
    public readonly aip: NewYorkAIP,
    public readonly planCode: PlanCode,
    public readonly planCodes: PlanCodes,
    public readonly additionalProperties: AdditionalProperties
  ) {}
}

export class ValidNewYorkApplication {
  public static createApp({
    caseDetails,
    applicantDemographics,
    ownerInfo,
    secondaryAddressee,
    primaryBeneficiaryList,
    primaryBeneficiary,
    contingentBeneficiary,
    contingentBeneficiaryList,
    criticalIllness,
    icrtn,
    cancer,
    dependents,
    dependentLists,
    policyRecord,
    billingAgent,
    agentInformation,
    agentList,
    messages,
    billingModes,
    awdStatus,
    verifyCriticalInformation,
    disability,
    shop,
    iwl,
    heartStroke,
    accident,
    uwQuestions,
    universalLife,
    glterm,
    pinCare,
    accidentNewYork,
    criticalIllnessNY,
    hippaTrustees,
    premium,
    aip,
    planCode,
    planCodes,
    additionalProperties,
  }: NewYorkProps) {
    return this.create(
      caseDetails,
      applicantDemographics,
      ownerInfo,
      secondaryAddressee,
      primaryBeneficiaryList,
      primaryBeneficiary,
      contingentBeneficiary,
      contingentBeneficiaryList,
      criticalIllness,
      icrtn,
      cancer,
      dependents,
      dependentLists,
      policyRecord,
      billingAgent,
      agentInformation,
      agentList,
      messages,
      billingModes,
      awdStatus,
      verifyCriticalInformation,
      disability,
      shop,
      iwl,
      heartStroke,
      accident,
      uwQuestions,
      universalLife,
      glterm,
      pinCare,
      accidentNewYork,
      criticalIllnessNY,
      hippaTrustees,
      premium,
      aip,
      planCode,
      planCodes,
      additionalProperties
    );
  }

  public static create(
    caseDetails: NewYorkCaseDetails,
    applicantDemographics: NewYorkApplicantDemographics,
    ownerInfo: OwnerInfo,
    secondaryAddressee: SecondaryAddressee,
    primaryBeneficiaryList: PrimaryBeneficiaryList,
    primaryBeneficiary: PrimaryBeneficiary,
    contingentBeneficiary: ContingentBeneficiary,
    contingentBeneficiaryList: ContingentBeneficiaryList,
    criticalIllness: NewYorkCriticalIllness,
    icrtn: NewYorkICRTN,
    cancer: NewYorkCancer,
    dependents: Dependents,
    dependentLists: DependentLists,
    policyRecord: PolicyRecord,
    billingAgent: NewYorkBillingAgent,
    agentInformation: AgentInformation,
    agentList: AgentList,
    messages: Messages,
    billingModes: BillingModes,
    awdStatus: NewYorkAWDStatus,
    verifyCriticalInformation: NewYorkVerifyCriticalInformation,
    disability: NewYorkDisability,
    shop: NewYorkShop,
    iwl: NewYorkIWL,
    heartStroke: NewYorkHeartStroke,
    accident: NewYorkAccident,
    uwQuestions: UWQuestions,
    universalLife: NewYorkUL,
    glterm: NewYorkGLTERM,
    pinCare: NewYorkPinCare,
    accidentNewYork: NewYorkAccidentNewYork,
    criticalIllnessNY: NewYorkCriticalIllnessNY,
    hippaTrustees: HippaTrustees,
    premium: Premium,
    aip: NewYorkAIP,
    planCode: PlanCode,
    planCodes: PlanCodes,
    additionalProperties: AdditionalProperties
  ) {
    return caseDetails instanceof ValidNewYorkCaseDetails &&
      applicantDemographics instanceof ValidNewYorkApplicantDemographics &&
      ownerInfo instanceof ValidOwnerInfo &&
      secondaryAddressee instanceof ValidSecondaryAddressee &&
      primaryBeneficiaryList instanceof ValidPrimaryBeneficiaryList &&
      primaryBeneficiary instanceof ValidPrimaryBeneficiary &&
      contingentBeneficiary instanceof ValidContingentBeneficiary &&
      contingentBeneficiaryList instanceof ValidContingentBeneficiaryList &&
      criticalIllness instanceof ValidNewYorkCriticalIllness &&
      icrtn instanceof ValidNewYorkICRTN &&
      cancer instanceof ValidNewYorkCancer &&
      dependents instanceof ValidDependents &&
      dependentLists instanceof ValidDependentList &&
      policyRecord instanceof ValidPolicyRecord &&
      billingAgent instanceof ValidNewYorkBillingAgent &&
      agentInformation instanceof ValidAgent &&
      agentList instanceof ValidAgentList &&
      messages instanceof ValidMessage &&
      billingModes instanceof ValidBillingModes &&
      awdStatus instanceof ValidNewYorkAWDStatus &&
      verifyCriticalInformation instanceof ValidNewYorkVerifyCriticalInformation &&
      disability instanceof ValidNewYorkDisability &&
      shop instanceof ValidNewYorkShop &&
      iwl instanceof ValidNewYorkIWL &&
      heartStroke instanceof ValidNewYorkHeartStroke &&
      accident instanceof ValidNewYorkAccident &&
      uwQuestions instanceof ValidUWQuestions &&
      universalLife instanceof ValidGroupUL &&
      glterm instanceof ValidGroupGLTERM &&
      pinCare instanceof ValidNewYorkPinCare &&
      accidentNewYork instanceof ValidNewYorkAccidentNewYork &&
      criticalIllnessNY instanceof ValidNewYorkCriticalIllnessNY &&
      hippaTrustees instanceof ValidHippaTrustees &&
      premium instanceof ValidPremium &&
      aip instanceof ValidNewYorkAIP &&
      planCode instanceof ValidPlanCode &&
      planCodes instanceof ValidPlanCodes &&
      additionalProperties instanceof ValidAdditionalProperties
      ? new ValidNewYorkApplication(
          caseDetails,
          applicantDemographics,
          ownerInfo,
          secondaryAddressee,
          primaryBeneficiaryList,
          primaryBeneficiary,
          contingentBeneficiary,
          contingentBeneficiaryList,
          criticalIllness,
          icrtn,
          cancer,
          dependents,
          dependentLists,
          policyRecord,
          billingAgent,
          agentInformation,
          agentList,
          messages,
          billingModes,
          awdStatus,
          verifyCriticalInformation,
          disability,
          shop,
          iwl,
          heartStroke,
          accident,
          uwQuestions,
          universalLife,
          glterm,
          pinCare,
          accidentNewYork,
          criticalIllnessNY,
          hippaTrustees,
          premium,
          aip,
          planCode,
          planCodes,
          additionalProperties
        )
      : new InvalidNewYorkApplication(
          caseDetails,
          applicantDemographics,
          ownerInfo,
          secondaryAddressee,
          primaryBeneficiaryList,
          primaryBeneficiary,
          contingentBeneficiary,
          contingentBeneficiaryList,
          criticalIllness,
          icrtn,
          cancer,
          dependents,
          dependentLists,
          policyRecord,
          billingAgent,
          agentInformation,
          agentList,
          messages,
          billingModes,
          awdStatus,
          verifyCriticalInformation,
          disability,
          shop,
          iwl,
          heartStroke,
          accident,
          uwQuestions,
          universalLife,
          glterm,
          pinCare,
          accidentNewYork,
          criticalIllnessNY,
          hippaTrustees,
          premium,
          aip,
          planCode,
          planCodes,
          additionalProperties
        );
  }

  public readonly key = 'ValidNewYorkApplication';

  private constructor(
    public readonly caseDetails: ValidNewYorkCaseDetails,
    public readonly applicantDemographics: ValidNewYorkApplicantDemographics,
    public readonly ownerInfo: ValidOwnerInfo,
    public readonly secondaryAddressee: ValidSecondaryAddressee,
    public readonly primaryBeneficiaryList: ValidPrimaryBeneficiaryList,
    public readonly primaryBeneficiary: ValidPrimaryBeneficiary,
    public readonly contingentBeneficiary: ValidContingentBeneficiary,
    public readonly contingentBeneficiaryList: ValidContingentBeneficiaryList,
    public readonly criticalIllness: NewYorkCriticalIllness,
    public readonly icrtn: ValidNewYorkICRTN,
    public readonly cancer: NewYorkCancer,
    public readonly dependents: Dependents,
    public readonly dependentLists: DependentLists,
    public readonly policyRecord: PolicyRecord,
    public readonly billingAgent: ValidNewYorkBillingAgent,
    public readonly agentInformation: ValidAgent,
    public readonly agentList: ValidAgentList,
    public readonly messages: ValidMessage,
    public readonly billingModes: ValidBillingModes,
    public readonly awdStatus: NewYorkAWDStatus,
    public readonly verifyCriticalInformation: ValidNewYorkVerifyCriticalInformation,
    public readonly disability: ValidNewYorkDisability,
    public readonly shop: ValidNewYorkShop,
    public readonly iwl: NewYorkIWL,
    public readonly heartStroke: ValidNewYorkHeartStroke,
    public readonly accident: ValidNewYorkAccident,
    public readonly uwQuestions: UWQuestions,
    public readonly universalLife: NewYorkUL,
    public readonly glterm: NewYorkGLTERM,
    public readonly pinCare: ValidNewYorkPinCare,
    public readonly accidentNewYork: ValidNewYorkAccidentNewYork,
    public readonly criticalIllnessNY: ValidNewYorkCriticalIllnessNY,
    public readonly hippaTrustees: HippaTrustees,
    public readonly premium: Premium,
    public readonly aip: ValidNewYorkAIP,
    public readonly planCode: PlanCode,
    public readonly planCodes: PlanCodes,
    public readonly additionalProperties: AdditionalProperties
  ) {}
}

export type NewYorkApplication = ValidNewYorkApplication | InvalidNewYorkApplication;

export type NewYorkApplicationOptions<TReturn> = ToMatchOptions<NewYorkApplication, 'key', TReturn>;

export const matchNewYorkApplication = <TReturn>(
  newYorkApplication: NewYorkApplication,
  options: NewYorkApplicationOptions<TReturn>
): TReturn => {
  switch (newYorkApplication.key) {
    case 'InvalidNewYorkApplication':
      return options.InvalidNewYorkApplication(newYorkApplication);
    case 'ValidNewYorkApplication':
      return options.ValidNewYorkApplication(newYorkApplication);
  }
};

export const changeStandardCaseDetails = (caseDetails: NewYorkCaseDetails) => (app: NewYorkApplication) =>
  ValidNewYorkApplication.createApp({...app, caseDetails});

export const changeApplicantDemographics = (applicantDemographics: NewYorkApplicantDemographics) => (
  app: NewYorkApplication
) => ValidNewYorkApplication.createApp({...app, applicantDemographics});

export const changeSecondaryAddressee = (secondaryAddressee: SecondaryAddressee) => (app: NewYorkApplication) =>
  ValidNewYorkApplication.createApp({...app, secondaryAddressee});

export const changePrimaryBeneficiaryList = (primaryBeneficiaryList: PrimaryBeneficiaryList) => (
  app: NewYorkApplication
) => ValidNewYorkApplication.createApp({...app, primaryBeneficiaryList});

export const changePrimaryBeneficiary = (primaryBeneficiary: PrimaryBeneficiary) => (app: NewYorkApplication) =>
  ValidNewYorkApplication.createApp({...app, primaryBeneficiary});

export const changeContingentBeneficiary = (contingentBeneficiary: ContingentBeneficiary) => (
  app: NewYorkApplication
) => ValidNewYorkApplication.createApp({...app, contingentBeneficiary});

export const changeContingentBeneficiaryList = (contingentBeneficiaryList: ContingentBeneficiaryList) => (
  app: NewYorkApplication
) => ValidNewYorkApplication.createApp({...app, contingentBeneficiaryList});

export const changeDependents = (dependents: Dependents) => (app: NewYorkApplication) =>
  ValidNewYorkApplication.createApp({...app, dependents});

export const changeDependentLists = (dependentLists: DependentLists) => (app: NewYorkApplication) =>
  ValidNewYorkApplication.createApp({...app, dependentLists});

export const changeCriticalIllness = (criticalIllness: NewYorkCriticalIllness) => (app: NewYorkApplication) =>
  ValidNewYorkApplication.createApp({...app, criticalIllness});

export const changeICRTN = (icrtn: NewYorkICRTN) => (app: NewYorkApplication) =>
  ValidNewYorkApplication.createApp({...app, icrtn});

export const changePolicyRecord = (policyRecord: PolicyRecord) => (app: NewYorkApplication) =>
  ValidNewYorkApplication.createApp({...app, policyRecord});

export const changeBillingAgent = (billingAgent: NewYorkBillingAgent) => (app: NewYorkApplication) =>
  ValidNewYorkApplication.createApp({...app, billingAgent});

export const changeAgent = (agentInformation: AgentInformation) => (app: NewYorkApplication) =>
  ValidNewYorkApplication.createApp({...app, agentInformation});

export const changeAgentList = (agentList: AgentList) => (app: NewYorkApplication) =>
  ValidNewYorkApplication.createApp({...app, agentList});

export const changeMessages = (messages: Messages) => (app: NewYorkApplication) =>
  ValidNewYorkApplication.createApp({...app, messages});

export const changeBillingModes = (billingModes: BillingModes) => (app: NewYorkApplication) =>
  ValidNewYorkApplication.createApp({...app, billingModes});

export const changeAWDStatus = (awdStatus: NewYorkAWDStatus) => (app: NewYorkApplication) =>
  ValidNewYorkApplication.createApp({...app, awdStatus});

export const changeVerifyCriticalInformation = (verifyCriticalInformation: NewYorkVerifyCriticalInformation) => (
  app: NewYorkApplication
) => ValidNewYorkApplication.createApp({...app, verifyCriticalInformation});

export const changeDisability = (disability: NewYorkDisability) => (app: NewYorkApplication) =>
  ValidNewYorkApplication.createApp({...app, disability});

export const changeShop = (shop: NewYorkShop) => (app: NewYorkApplication) =>
  ValidNewYorkApplication.createApp({...app, shop});

export const changeIWL = (iwl: NewYorkIWL) => (app: NewYorkApplication) =>
  ValidNewYorkApplication.createApp({...app, iwl});

export const changeCancer = (cancer: NewYorkCancer) => (app: NewYorkApplication) =>
  ValidNewYorkApplication.createApp({...app, cancer});

export const changeHeartStroke = (heartStroke: NewYorkHeartStroke) => (app: NewYorkApplication) =>
  ValidNewYorkApplication.createApp({...app, heartStroke});

export const changeAccident = (accident: NewYorkAccident) => (app: NewYorkApplication) =>
  ValidNewYorkApplication.createApp({...app, accident});

export const changeUWQuestions = (uwQuestions: UWQuestions) => (app: NewYorkApplication) =>
  ValidNewYorkApplication.createApp({...app, uwQuestions});

export const changeUniversalLife = (universalLife: NewYorkUL) => (app: NewYorkApplication) =>
  ValidNewYorkApplication.createApp({...app, universalLife});

export const changeGLTERM = (glterm: NewYorkGLTERM) => (app: NewYorkApplication) =>
  ValidNewYorkApplication.createApp({...app, glterm});

export const changePinCare = (pinCare: NewYorkPinCare) => (app: NewYorkApplication) =>
  ValidNewYorkApplication.createApp({...app, pinCare});

export const changeAccidentNewYork = (accidentNewYork: NewYorkAccidentNewYork) => (app: NewYorkApplication) =>
  ValidNewYorkApplication.createApp({...app, accidentNewYork});

export const changeCriticalIllnessNY = (criticalIllnessNY: NewYorkCriticalIllnessNY) => (app: NewYorkApplication) =>
  ValidNewYorkApplication.createApp({...app, criticalIllnessNY});

export const changeHippaTrustees = (hippaTrustees: HippaTrustees) => (app: NewYorkApplication) =>
  ValidNewYorkApplication.createApp({...app, hippaTrustees});

export const changePremium = (premium: Premium) => (app: NewYorkApplication) =>
  ValidNewYorkApplication.createApp({...app, premium});
export const changeAIP = (aip: NewYorkAIP) => (app: NewYorkApplication) =>
  ValidNewYorkApplication.createApp({...app, aip});

export const changePlanCode = (planCode: PlanCode) => (app: NewYorkApplication) =>
  ValidNewYorkApplication.createApp({...app, planCode});

export const changePlanCodes = (planCodes: PlanCodes) => (app: NewYorkApplication) =>
  ValidNewYorkApplication.createApp({...app, planCodes});

export const changeAdditionalProperties = (additionalProperties: AdditionalProperties) => (app: NewYorkApplication) =>
  ValidNewYorkApplication.createApp({...app, additionalProperties});

export const EmptyNewYorkApplication = new InvalidNewYorkApplication(
  EmptyNewYorkCaseDetails,
  EmptyNewYorkApplicantDemographics,
  EmptyOwnerInfo,
  EmptySecondaryAddressee,
  EmptyPrimaryBeneficiaryList,
  EmptyPrimaryBeneficiary,
  EmptyContingentBeneficiary,
  EmptyContingentBeneficiaryList,
  EmptyNewYorkCriticalIllness,
  EmptyNewYorkICRTN,
  EmptyNewYorkCancer,
  EmptyDependents,
  EmptyDependentList,
  EmptyPolicyRecord,
  EmptyNewYorkBillingAgent,
  EmptyAgent,
  EmptyAgentList,
  EmptyMessage,
  EmptyBillingModes,
  EmptyNewYorkAWDStatus,
  EmptyNewYorkVerifyCriticalInformation,
  EmptyNewYorkDisability,
  EmptyNewYorkShop,
  EmptyNewYorkIWL,
  EmptyNewYorkHeartStroke,
  EmptyNewYorkAccident,
  EmptyUWQuestions,
  EmptyNewYorkUL,
  EmptyNewYorkGLTERM,
  EmptyNewYorkPinCare,
  EmptyNewYorkAccidentNewYork,
  EmptyNewYorkCriticalIllnessNY,
  EmptyHippaTrustees,
  EmptyPremium,
  EmptyNewYorkAIP,
  EmptyPlanCode,
  EmptyPlanCodes,
  EmptyAdditionalProperties
);
